import React, { useCallback, useState } from 'react';
import { Alert, Button, Card, Form, Table } from 'react-bootstrap';
import { Link, NavLink, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { Details, PageTitle, ResourceState, formatDate } from '../TenantAdmin/shared';
import { OfferingFields, useConfirmedMutation, usePlatformApi, usePlatformResource } from './shared';
import { platformError } from './api';
import AnalyticsDashboard from '../Analytics/AnalyticsDashboard';

export function Dashboard() {
  return <section className="ta-page"><PageTitle title="Platform dashboard" /><AnalyticsDashboard embedded title="Platform analytics" />
    <div className="ta-actions"><Link to="/platform-admin/tenants">View tenants</Link><Link to="/platform-admin/tenants/new">Create tenant</Link></div>
  </section>;
}
export function TenantList() {
  const api = usePlatformApi(); const resource = usePlatformResource(useCallback(() => api.tenants(), [api]));
  return <section className="ta-page"><PageTitle title="Tenants" /><p>The API returns the tenant list without server-side search or filters.</p>
    <div className="ta-actions"><Link to="/platform-admin/tenants/new">Create tenant</Link><Button disabled={resource.loading} onClick={resource.reload}>Reload tenants</Button></div>
    <ResourceState resource={resource}>{resource.data?.length ? <Table responsive className="ta-table"><caption>Customer tenants</caption><thead><tr>
      {['Tenant', 'Domain', 'Status', 'Offering', 'Subscription', 'Allocated seats', 'Authentication'].map(title => <th scope="col" key={title}>{title}</th>)}
    </tr></thead><tbody>{resource.data.map(t => <tr key={t.id}><td><Link to={`/platform-admin/tenants/${t.id}`}>{t.name}</Link></td><td>{t.domain || 'Not provided'}</td><td>{t.status}</td>
      <td>{t.subscription?.offeringCode || 'Not provided'}</td><td>{t.subscription?.status || 'Not provided'}</td><td>{t.subscription?.allocatedSeats ?? 'Not provided'}</td><td>{t.authenticationMode || 'Not configured'}</td>
    </tr>)}</tbody></Table> : <p>No tenants to display.</p>}</ResourceState>
  </section>;
}

export function CreateTenant() {
  const api = usePlatformApi(); const resource = usePlatformResource(useCallback(() => api.offerings(), [api]));
  const [form, setForm] = useState({ companyName: '', domain: '', adminEmail: '', adminName: '', offeringCode: '', allocatedSeats: 3 });
  const [created, setCreated] = useState(null); const [copied, setCopied] = useState(false); const mutation = useConfirmedMutation();
  const submit = event => {
    event.preventDefault();
    const payload = { ...form, companyName: form.companyName.trim(), domain: form.domain.trim(), adminEmail: form.adminEmail.trim(), adminName: form.adminName.trim() };
    mutation.request({ title: 'Create customer tenant?', description: `Create ${payload.companyName} with ${payload.offeringCode} and ${payload.allocatedSeats} seats? Creation does not complete onboarding or grant a usable tenant session.`,
      run: async () => setCreated(await api.createTenant(payload)), success: 'Tenant created. Continue the backend-controlled onboarding process.' });
  };
  const setupLink = created?.invitationToken ? `${window.location.origin}/setup/invitation?token=${encodeURIComponent(created.invitationToken)}` : null;
  const copySetupLink = async () => {
    if (!setupLink) return;
    try {
      await navigator.clipboard.writeText(setupLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      /* noop */
    }
  };
  return <section className="ta-page"><PageTitle title="Create tenant" />{mutation.feedback}
    <p>New tenants remain in PENDING_SETUP until the backend-controlled activation flow is completed. A subscription alone does not complete onboarding.</p>
    {created ? <Card><Card.Body><h2>Tenant created</h2><Details values={[
      ['Name', created.name], ['Status', created.status], ['Offering', created.subscription?.offeringCode], ['Allocated seats', created.subscription?.allocatedSeats],
      ['Starts', formatDate(created.subscription?.startsAt)], ['Ends', formatDate(created.subscription?.endsAt)],
    ]} />
    {setupLink ? <div className="pa-setup-link mt-3">
      <h3>Tenant Admin Setup Link</h3>
      <p>Share this one-time setup link with the initial Tenant Admin. This link is single-use and expires {formatDate(created.invitationExpiresAt)}.</p>
      <div className="ta-actions">
        <Button variant="outline-primary" onClick={copySetupLink}>{copied ? 'Copied setup link' : 'Copy Setup Link'}</Button>
        <a href={setupLink} target="_blank" rel="noopener noreferrer">Open setup page</a>
      </div>
    </div> : <p>This endpoint does not return an administrator setup link or token in this environment. Complete the existing controlled onboarding process; no email delivery is implied.</p>}
      <div className="ta-actions mt-3"><Link to={`/platform-admin/tenants/${created.id}`}>Open tenant details</Link></div></Card.Body></Card> : <ResourceState resource={resource}>
      {resource.data?.length ? <Card><Card.Body><Form onSubmit={submit} aria-label="Create tenant">
        {[
          ['companyName', 'Organization name', 'text'], ['domain', 'Primary domain', 'text'],
          ['adminName', 'Initial Tenant Admin name', 'text'], ['adminEmail', 'Initial Tenant Admin email', 'email'],
        ].map(([key, label, type]) => <Form.Group key={key} controlId={`create-${key}`}><Form.Label>{label}</Form.Label><Form.Control required type={type} disabled={mutation.busy} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} /></Form.Group>)}
        <OfferingFields offerings={resource.data} value={form} onChange={setForm} disabled={mutation.busy} />
        <p>Authentication mode is configured separately after creation. No prices or automatic activation are assigned here.</p>
        <Button type="submit" disabled={mutation.busy}>Review creation</Button>
      </Form></Card.Body></Card> : <p>No active offerings are available.</p>}
    </ResourceState>}
  </section>;
}

export function TenantBoundary() {
  const { tenantId } = useParams();
  if (!/^[1-9]\d*$/.test(tenantId)) return <section className="ta-page"><h1>Invalid tenant identifier</h1></section>;
  return <TenantRecord key={tenantId} id={tenantId} />;
}
function TenantRecord({ id }) {
  const api = usePlatformApi(); const resource = usePlatformResource(useCallback(() => api.tenant(id), [api, id]));
  const [updated, setUpdated] = useState(null); const [reloadError, setReloadError] = useState('');
  const refresh = useCallback(async () => {
    try { setUpdated(await api.tenant(id)); setReloadError(''); }
    catch (error) { setReloadError(`The change may be saved, but the latest tenant details could not be loaded. ${platformError(error)}`); }
  }, [api, id]);
  const tenant = updated || resource.data;
  return <section className="ta-page"><ResourceState resource={resource}>{tenant ? <>
    <PageTitle title={tenant.name} /><p>Customer tenant ID: {tenant.id}. This is a platform record, not an impersonated tenant session.</p>
    <nav className="pa-record-nav" aria-label="Tenant record">
      {[
        ['', 'Details'], ['/analytics', 'Analytics'], ['/subscription', 'Subscription'], ['/authentication', 'Authentication'], ['/sso', 'SSO'], ['/tenant-admin', 'Tenant Admin'],
      ].map(([suffix, title]) => <NavLink end key={title} to={`/platform-admin/tenants/${id}${suffix}`}>{title}</NavLink>)}
    </nav>
    {reloadError && <Alert variant="danger">{reloadError} <Button onClick={refresh}>Reload tenant details</Button></Alert>}
    <Outlet context={{ tenant, id, api, refresh }} />
  </> : <p>No tenant record was returned.</p>}</ResourceState></section>;
}
export const useTenantRecord = () => useOutletContext();

export function TenantDetail() {
  const { tenant, id, api, refresh } = useTenantRecord();
  const [status, setStatus] = useState(''); const mutation = useConfirmedMutation();
  return <><h2>Tenant details</h2><Card><Card.Body><Details values={[
    ['Tenant ID', tenant.id], ['Organization name', tenant.name], ['Primary domain', tenant.domain], ['Tenant status', tenant.status],
    ['Authentication mode', tenant.authenticationMode || 'Not configured'], ['Offering', tenant.subscription?.offeringCode],
    ['Subscription status', tenant.subscription?.status], ['Allocated seats', tenant.subscription?.allocatedSeats],
    ['SSO configured', tenant.ssoConfigured ? 'Yes' : 'No'], ['Current Tenant Admin', 'Not returned by the platform API'],
  ]} /><p>Used seats, administrator identity and creation/update timestamps are not returned by this detail endpoint.</p></Card.Body></Card>
    {mutation.feedback}<Card><Card.Body><h2>Tenant status</h2><Form onSubmit={event => {
      event.preventDefault(); const next = status;
      mutation.request({ title: 'Change tenant status?', description: `Set ${tenant.name} (ID ${id}) to ${next}? Deactivation revokes tenant refresh sessions. Activation and access remain backend-controlled.`,
        run: async () => { await api.status(id, next); await refresh(); }, success: 'Tenant status change accepted.' });
    }}>
      <Form.Group controlId="tenant-status"><Form.Label>New tenant status</Form.Label><Form.Select required value={status} disabled={mutation.busy} onChange={e => setStatus(e.target.value)}><option value="">Select status</option><option>ACTIVE</option><option>DEACTIVATED</option></Form.Select></Form.Group>
      <p>Only ACTIVE and DEACTIVATED are supported by the tenant status API. The server decides whether the request is valid.</p>
      <Button type="submit" disabled={mutation.busy}>Review status change</Button>
    </Form></Card.Body></Card>
  </>;
}
